// Common
import { type HttpContext } from '@angular/common/http';
import { Component, inject, Input, OnInit } from '@angular/core';
import { heightAnimation } from '@modules/common/animations/height.animation';
import { mailboxTenantContext, owningTenantContext } from '@modules/common/interceptors/spaces.interceptor';

// Services
import { Constants } from '@frontendlib/common/types/Constants';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import { StitchEventsService } from '@modules/common/services/stitch-events-service';
import { SignalStitchEventType } from '@sharedlib/common/types/signal-type';
import { ContainerDndService, CROSS_TENANT_DROP_TEXT } from '@modules/common/services/container-dnd.service';
import { StitchService } from '@modules/common/services/stitch.service';
import { MainViewService } from '@modules/common/services/main-view.service';
import { TaskingAppStateService } from '@modules/tasks/services/state.service';
import { AlertService } from '@modules/alert/services/alert.service';
import { ProjectsService } from '@modules/tasks/services/projects.service';
import { TasksService } from '@modules/tasks/services/tasks.service';

// Types
import { Application } from '@modules/common/types/application';
import { Stitch } from '@modules/common/types/stitch';
import { DragData, dragDataTypeAllowed, DragDataTypes, getStringField } from '@modules/drag-n-drop/types/drag-data';
import { StateKey } from '@modules/settings/types/state-key';
import { Project } from '@modules/tasks/types/project';
import { ProjectsFilters } from '@modules/tasks/types/projects-filters';
import { Task } from '@modules/tasks/types/task';
import { VirtualFolder } from '@modules/tasks/types/virtual-folder';

// RX
import { debounceTime, filter, take, takeUntil } from 'rxjs/operators';

// Components
import { BaseSidebarContainersTreeComponent } from '@modules/common/components/base-sidebar-containers-tree/base-sidebar-containers-tree.component';
import { moveItemsIntoContainer } from '@modules/common/utils/move-into-container.util';

@Component({
  selector: 'app-side-bar-projects-tree',
  templateUrl: './side-bar-projects-tree.component.html',
  styleUrls: ['./side-bar-projects-tree.component.less'],
  animations: [heightAnimation],
  standalone: false,
})
export class SideBarProjectsTreeComponent
  extends BaseSidebarContainersTreeComponent<Project, Task, ProjectsFilters, VirtualFolder>
  implements OnInit
{
  @Input() public tenantId: string | null | undefined;

  private readonly taskingDropToaster = inject(ToasterService);
  private readonly railEvents = inject(StitchEventsService);
  private readonly copyDropService = inject(ContainerDndService);
  private readonly copyAlertService = inject(AlertService);
  private readonly taskCopyStitchService = inject(StitchService);
  private readonly mainView = inject(MainViewService);
  private readonly taskingState = inject(TaskingAppStateService, { optional: true });
  private restoredProjectSelection = false;

  public override ngOnInit(): void {
    super.ngOnInit();
    this.railEvents
      .getEvents(() => this.tenantId ?? this.spaceContextStore.contextId(), this.projectsService.stitchType, [
        SignalStitchEventType.MOVED_TO_TRASH,
        SignalStitchEventType.RESTORED_FROM_TRASH,
      ])
      .pipe(
        debounceTime(Constants.DEBOUNCE_EVENT_LIST_RELOAD),
        filter((event) => event.spaceId === (this.tenantId ?? this.spaceContextStore.contextId())),
        takeUntil(this.alive),
      )
      .subscribe(() => this.projectsService.forceRefresh());
  }

  selfDragDataTypes: DragDataTypes[] = [DragDataTypes.task, DragDataTypes.project];
  containerDragDataType = DragDataTypes.project;
  application = Application.tasking;
  treeStateKey = StateKey.taskingSidebarProjectsTree;

  public dndPredicate =
    (stitchItem: Stitch) =>
    (dragData: DragData): boolean =>
      stitchItem &&
      // Reject only a project dropped onto itself; task drops are accepted so tasks can be
      // moved into a project from the sidebar (matching Notes/Contacts/Files/Calendar).
      !(
        dragData.type === DragDataTypes.project &&
        dragData.data.length === 1 &&
        dragData.data?.[0]?.id === stitchItem?.id
      ) &&
      dragDataTypeAllowed(dragData.type) &&
      (dragData.type === DragDataTypes.task || !this.isCrossTenantDrop(dragData, stitchItem));

  constructor(
    private projectsService: ProjectsService,
    private tasksService: TasksService,
  ) {
    super(projectsService);
  }

  protected override requestContext(): HttpContext | undefined {
    return this.tenantId === undefined ? undefined : mailboxTenantContext(this.tenantId);
  }

  protected override beforeContainerSelectionApplied(): void {
    const selected = this.selectableList()?.selectableService.getSelectedItemsSync();
    const project = this.items.find(item => item.id === selected?.[0]?.identifier);
    // Selection changes the list filter before appClick's delayed open. Carry the same project
    // first, so the first list request already has its owner rather than the surrounding team.
    if (project) {
      this.taskingState?.setTaskCreationProject(project);
      this.stateService.setMainView(project);
    }
  }

  protected override onItemsReceived(): void {
    if (this.restoredProjectSelection || !this.items.length) return;
    this.restoredProjectSelection = true;
    const project = this.items.find(item => this.selectedItemsIds.includes(item.id));
    if (project) this.taskingState?.setTaskCreationProject(project);
    // Restore the selected project's owner when the rail resolves after a reload. Later rail
    // refreshes must not reopen a project the person has closed or replace an open task.
    if (project && !this.mainView.getCurrentMainView()) this.stateService.setMainView(project);
  }

  private isCrossTenantDrop(data: DragData, destination: Stitch): boolean {
    if (!this.selfDragDataTypes.includes(data.type)) return false;
    const destinationOwner = destination.accountId ?? this.tenantId;
    return (
      !!destinationOwner &&
      data.data.some((item) => {
        const sourceOwner = getStringField(item, 'accountId');
        return sourceOwner !== undefined && sourceOwner !== destinationOwner;
      })
    );
  }

  private refuseCrossTenantDrop(data: DragData, destination: Stitch): boolean {
    if (!this.isCrossTenantDrop(data, destination)) return false;
    this.taskingDropToaster.show({ text: CROSS_TENANT_DROP_TEXT.moveTooltip });
    return true;
  }

  public override dndDrop(data: DragData, destination: Stitch): void {
    if (data.type === DragDataTypes.task) {
      this.copyDropService.handleChildItemDrop(destination, data, {
        containerType: DragDataTypes.project, childItemType: DragDataTypes.task,
        childService: this.tasksService, containerIdField: 'projectId', crossTenantCopySupported: true,
        cancelled: this.alive, isActive: () => !this.alive.isStopped,
      }, this.taskCopyStitchService, this.copyAlertService);
      return;
    }
    if (this.refuseCrossTenantDrop(data, destination)) return;
    super.dndDrop(data, destination);
  }

  /**
   * Actions
   */

  public handleMove(dragData: DragData, item: Project) {
    if (this.refuseCrossTenantDrop(dragData, item)) return;
    const message = 'Successfully moved to ' + item.title;
    const requestContext = owningTenantContext(item.accountId) ?? this.requestContext();

    if (dragData.type === DragDataTypes.task) {
      const ids = this.getIds(dragData.data);
      moveItemsIntoContainer(this.tasksService, dragData.data, () =>
        this.tasksService.bunchUpdate({ ids }, { projectId: item.id }, { message, requestContext }),
      );
    } else if (dragData.type === DragDataTypes.project) {
      this.projectsService.bunchUpdate(
        { ids: this.getIds(dragData.data) },
        { parentId: item.id },
        { message, requestContext },
      );
    }
  }

  public handleDuplicate(dragData: DragData, folder: Project) {
    if (this.refuseCrossTenantDrop(dragData, folder)) return;
    const requestContext = owningTenantContext(folder.accountId) ?? this.requestContext();
    if (dragData.type === DragDataTypes.task) {
      this.tasksService.duplicate(this.getIds(dragData.data), folder.id, { requestContext }).pipe(take(1)).subscribe();
    } else if (dragData.type === DragDataTypes.project) {
      this.projectsService
        .duplicate(this.getIds(dragData.data), undefined, { requestContext })
        .pipe(take(1))
        .subscribe();
    }
  }
}
